import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    FiChevronLeft, FiChevronRight, FiCalendar, FiClock,
    FiRefreshCw
} from "react-icons/fi";
import { MdAccessTime, MdClose, MdContentCut, MdCalendarToday, MdPhone, MdCheck, MdDelete } from "react-icons/md";

import { appointmentsAPI } from "services/api";

// ============================================================
// YARDIMCI FONKSİYONLAR
// ============================================================

const formatISODate = (date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
};

const extractTime = (timeStr) => {
    if (!timeStr) return "00:00";
    const timeMatch = timeStr.match(/(\d{2}:\d{2})/);
    return timeMatch ? timeMatch[1] : "00:00";
};

const timeToFloat = (timeStr) => {
    if (!timeStr) return 0;
    const safeTime = extractTime(timeStr);
    const [hours, minutes] = safeTime.split(":").map(Number);
    return (hours || 0) + (minutes || 0) / 60;
};

const addMinutesToTime = (timeStr, minutesToAdd) => {
    if (!timeStr) return "";
    const safeTime = extractTime(timeStr);
    const [hours, minutes] = safeTime.split(":").map(Number);
    const date = new Date();
    date.setHours(hours || 0, minutes || 0, 0, 0);
    date.setMinutes(date.getMinutes() + minutesToAdd);
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const doAppointmentsOverlap = (a1, a2) => {
    const duration1 = Number(a1.duration) || Number(a1.service?.duration) || 30;
    const duration2 = Number(a2.duration) || Number(a2.service?.duration) || 30;

    const start1 = timeToFloat(a1.appointment_time);
    const end1 = start1 + (duration1 / 60);
    const start2 = timeToFloat(a2.appointment_time);
    const end2 = start2 + (duration2 / 60);
    return Math.max(start1, start2) < Math.min(end1, end2);
};

const calculateOverlaps = (appointments) => {
    const overlapGroups = [];
    appointments.forEach(appt => {
        let placed = false;
        for (const group of overlapGroups) {
            if (group.some(existingAppt => doAppointmentsOverlap(appt, existingAppt))) {
                group.push(appt);
                placed = true;
                break;
            }
        }
        if (!placed) overlapGroups.push([appt]);
    });
    return overlapGroups;
};

// ============================================================
// GRID AYARLARI (Okunabilirlik İçin Geniş Tutuldu)
// ============================================================
const START_HOUR = 8;
const END_HOUR = 23;
const HOUR_HEIGHT = 140; // 1 Saat = 140px (15 dakika = 35px)

const timeSlots = [];
for (let h = START_HOUR; h < END_HOUR; h++) {
    for (let m = 0; m < 60; m += 15) {
        timeSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
}

// ============================================================
// SKELETON LOADER
// ============================================================
const CalendarSkeleton = () => (
    <div className="flex flex-col h-full w-full p-4 gap-4 animate-pulse bg-gray-50 dark:bg-navy-900">
        <div className="h-20 w-full rounded-2xl bg-gray-200 dark:bg-navy-700" />
        <div className="flex-1 w-full rounded-2xl bg-gray-200 dark:bg-navy-700" />
    </div>
);

// ============================================================
// ANA BİLEŞEN
// ============================================================
const StaffCalendar = () => {
    const scrollRef = useRef(null);

    // --- STATELER ---
    const [currentDate, setCurrentDate] = useState(new Date());
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal Stateleri
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedAppt, setSelectedAppt] = useState(null);
    const [processing, setProcessing] = useState(false);

    // ============================================================
    // API İSTEKLERİ
    // ============================================================
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await appointmentsAPI.getMyAppointments();
            setAppointments(res?.appointments || res?.data || []);
        } catch (error) {
            console.error("Veri yükleme hatası:", error);
            toast.error("Takvim verileri yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // O anki saate otomatik kaydırma
    useEffect(() => {
        if (!loading && scrollRef.current) {
            const now = new Date();
            if (now.toDateString() === currentDate.toDateString()) {
                const currentHour = now.getHours();
                const currentMinute = now.getMinutes();
                if (currentHour >= START_HOUR && currentHour < END_HOUR) {
                    const topPos = (currentHour - START_HOUR + currentMinute / 60) * HOUR_HEIGHT;
                    scrollRef.current.scrollTop = Math.max(0, topPos - 100);
                }
            }
        }
    }, [loading, currentDate]);

    // ============================================================
    // FİLTRELEME VE GÖRSELLEŞTİRME
    // ============================================================
    const currDateStr = formatISODate(currentDate);

    // Ekranda sadece seçili tarihteki randevuları göster
    const displayedAppointments = useMemo(() => {
        return appointments.filter(appt => {
            if (appt.status === "cancelled" || appt.status === "rejected") return false;

            const apptDate = appt.appointment_date?.split('T')[0];
            return apptDate === currDateStr;
        });
    }, [appointments, currDateStr]);

    const overlapGroups = useMemo(() => calculateOverlaps(displayedAppointments), [displayedAppointments]);

    const getApptStyle = (appt) => {
        const group = overlapGroups.find(g => g.includes(appt));
        let width = 100;
        let left = 0;
        let zIndex = 10;

        if (group && group.length > 1) {
            const index = group.indexOf(appt);
            width = 100 / group.length;
            left = width * index;
            zIndex = 10 + index;
        }

        // BUGS ÇÖZÜMÜ BURADA: Duration bilgisini sağlamlaştırıyoruz
        const duration = Number(appt.duration) || Number(appt.service?.duration) || 30;

        const startFloat = timeToFloat(appt.appointment_time);
        const top = Math.max(0, (startFloat - START_HOUR) * HOUR_HEIGHT);
        const height = (duration / 60) * HOUR_HEIGHT;

        return {
            top: `${top}px`,
            height: `${height}px`,
            left: `${left}%`,
            width: `calc(${width}% - 2px)`,
            zIndex
        };
    };

    const getStatusColors = (status) => {
        switch (status) {
            case "pending": return "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700/50";
            case "approved": return "bg-brand-100 text-brand-800 border-brand-300 dark:bg-brand-900/40 dark:text-brand-300 dark:border-brand-700/50";
            case "completed": return "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700/50";
            case "no_show": return "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700/50";
            default: return "bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600";
        }
    };

    // ============================================================
    // İŞLEMLER
    // ============================================================
    const prevDay = () => {
        const d = new Date(currentDate);
        d.setDate(d.getDate() - 1);
        setCurrentDate(d);
    };

    const nextDay = () => {
        const d = new Date(currentDate);
        d.setDate(d.getDate() + 1);
        setCurrentDate(d);
    };

    const updateStatus = async (status) => {
        setProcessing(true);
        try {
            await appointmentsAPI.updateStatus(selectedAppt.id, status);
            toast.success(status === 'approved' ? "Randevu başarıyla onaylandı." : "Randevu iptal edildi.");
            setDetailsModalOpen(false);
            fetchData();
        } catch (error) {
            toast.error("İşlem gerçekleştirilirken bir hata oluştu.");
        } finally {
            setProcessing(false);
        }
    };

    // ============================================================
    // RENDER EKRANI
    // ============================================================
    if (loading && appointments.length === 0) {
        return <CalendarSkeleton />;
    }

    return (
        <div className="flex flex-col h-[100dvh] w-full bg-white dark:bg-navy-900 overflow-hidden relative">
            <ToastContainer position="top-right" autoClose={2000} hideProgressBar theme="colored" />

            {/* 1. ÜST BAR: TARİH SEÇİMİ */}
            <div className="flex-none bg-white dark:bg-navy-800 shadow-sm border-b border-gray-100 dark:border-white/10 z-20">
                <div className="flex items-center justify-between p-4">
                    <button onClick={prevDay} className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 dark:bg-navy-700 text-gray-600 dark:text-gray-300 hover:bg-brand-500 hover:text-white transition-all active:scale-95 border border-gray-200 dark:border-white/5">
                        <FiChevronLeft size={20} />
                    </button>

                    <div className="flex flex-col items-center">
                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-0.5">
                            {currentDate.toLocaleDateString("tr-TR", { weekday: "long" })}
                        </span>
                        <span className="text-lg font-black text-navy-700 dark:text-white flex items-center gap-2">
                            <FiCalendar className="text-brand-500" />
                            {currentDate.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                        </span>
                    </div>

                    <button onClick={nextDay} className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-50 dark:bg-navy-700 text-gray-600 dark:text-gray-300 hover:bg-brand-500 hover:text-white transition-all active:scale-95 border border-gray-200 dark:border-white/5">
                        <FiChevronRight size={20} />
                    </button>
                </div>
            </div>

            {/* 2. KAYDIRILABİLİR TAKVİM IZGARASI */}
            <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-navy-900 relative p-2" ref={scrollRef}>
                {loading ? (
                    <div className="flex justify-center py-10">
                        <div className="h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <div className="relative w-full bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 overflow-hidden" style={{ height: `${(END_HOUR - START_HOUR) * HOUR_HEIGHT}px` }}>

                        {/* Saat Çizgileri */}
                        {timeSlots.map((time, index) => {
                            const isHour = time.endsWith(":00");
                            return (
                                <div
                                    key={time}
                                    className="absolute w-full flex border-t border-gray-100 dark:border-navy-700/50"
                                    style={{ top: `${index * (HOUR_HEIGHT / 4)}px`, height: `${HOUR_HEIGHT / 4}px` }}
                                >
                                    <div className="w-14 flex-none relative bg-gray-50 dark:bg-navy-900/50 border-r border-gray-100 dark:border-navy-700/50">
                                        {isHour && (
                                            <span className="absolute -top-2.5 right-2 text-[10px] font-bold text-gray-400 dark:text-gray-500">
                                                {time}
                                            </span>
                                        )}
                                    </div>
                                    <div className={`flex-1 ${!isHour ? 'border-t border-dashed border-gray-100 dark:border-navy-700/30' : ''}`} />
                                </div>
                            );
                        })}

                        {/* Randevu Kartları */}
                        <div className="absolute top-0 bottom-0 right-0 left-14 p-1 pointer-events-none">
                            {displayedAppointments.map((appt) => {
                                const style = getApptStyle(appt);
                                const statusColor = getStatusColors(appt.status);
                                const duration = Number(appt.duration) || Number(appt.service?.duration) || 30;

                                return (
                                    <div
                                        key={appt.id}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedAppt(appt);
                                            setDetailsModalOpen(true);
                                        }}
                                        className={`absolute rounded-xl border p-2 shadow-sm cursor-pointer transition-transform active:scale-95 flex flex-col justify-start overflow-hidden pointer-events-auto ${statusColor}`}
                                        style={style}
                                    >
                                        <div className="text-[10px] font-black opacity-80 leading-none mb-0.5 flex items-center gap-1">
                                            <FiClock size={10} /> {extractTime(appt.appointment_time)} - {addMinutesToTime(appt.appointment_time, duration)}
                                        </div>
                                        <div className="text-xs font-bold leading-tight truncate drop-shadow-sm">
                                            {appt.customer?.name} {appt.customer?.surname}
                                        </div>
                                        {duration >= 30 && (
                                            <div className="text-[10px] font-medium opacity-90 truncate mt-0.5">
                                                {appt.service?.name}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Yüzen Yenileme Butonu */}
            <div className="absolute bottom-6 right-6 flex flex-col gap-3 z-30">
                <button
                    onClick={fetchData}
                    className="h-14 w-14 rounded-full bg-brand-500 text-white shadow-xl shadow-brand-500/40 flex items-center justify-center hover:bg-brand-600 active:scale-95 transition-all"
                >
                    <FiRefreshCw size={24} className={loading ? "animate-spin" : ""} />
                </button>
            </div>

            {/* ============================================================ */}
            {/* RANDEVU DETAY MODALI (Personel Onay/İptal İşlemleri) */}
            {/* ============================================================ */}
            {detailsModalOpen && selectedAppt && (
                <div className="fixed inset-0 z-[100] flex items-end justify-center bg-navy-900/60 backdrop-blur-sm sm:items-center p-4">
                    <div className="w-full max-w-md bg-white dark:bg-navy-800 rounded-[30px] shadow-2xl overflow-hidden transform transition-all animate-slideUp flex flex-col">

                        <div className="p-6 bg-gradient-to-r from-brand-500 to-brand-600 dark:from-navy-700 dark:to-navy-800 text-white relative">
                            <button onClick={() => setDetailsModalOpen(false)} className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors">
                                <MdClose className="text-xl" />
                            </button>
                            <h3 className="text-xl font-black mb-1">{selectedAppt.customer?.name} {selectedAppt.customer?.surname}</h3>
                            {selectedAppt.customer?.phone && (
                                <a href={`tel:${selectedAppt.customer.phone}`} className="flex items-center gap-2 text-sm font-medium opacity-90 hover:opacity-100 transition-opacity">
                                    <MdPhone /> {selectedAppt.customer.phone}
                                </a>
                            )}
                        </div>

                        <div className="p-6 flex flex-col gap-4">
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-navy-900/50 border border-gray-100 dark:border-white/5">
                                <div className="h-10 w-10 flex-none rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-500 flex items-center justify-center text-xl"><MdCalendarToday /></div>
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase">Tarih & Saat</p>
                                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                                        {formatISODate(selectedAppt.appointment_date)} | {extractTime(selectedAppt.appointment_time)} - {addMinutesToTime(selectedAppt.appointment_time, Number(selectedAppt.duration) || Number(selectedAppt.service?.duration) || 30)}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-navy-900/50 border border-gray-100 dark:border-white/5">
                                <div className="h-10 w-10 flex-none rounded-full bg-orange-50 dark:bg-orange-500/10 text-orange-500 flex items-center justify-center text-xl"><MdContentCut /></div>
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase">Hizmet</p>
                                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                                        {selectedAppt.service?.name}
                                    </p>
                                </div>
                            </div>

                            {selectedAppt.note && (
                                <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 text-blue-700 dark:text-blue-300 text-sm font-medium italic">
                                    "{selectedAppt.note}"
                                </div>
                            )}
                        </div>

                        {/* AKSİYON BUTONLARI (Onayla / İptal) */}
                        <div className="p-4 bg-gray-50 dark:bg-navy-900 border-t border-gray-100 dark:border-white/5 flex flex-col gap-3">

                            {selectedAppt.status === 'pending' && (
                                <button
                                    onClick={() => updateStatus('approved')}
                                    disabled={processing}
                                    className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-green-500 text-white text-sm font-bold shadow-lg shadow-green-500/30 hover:bg-green-600 transition-colors disabled:opacity-50"
                                >
                                    {processing ? <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"/> : <><MdCheck className="text-xl"/> Randevuyu Onayla</>}
                                </button>
                            )}

                            {selectedAppt.status !== 'cancelled' && (
                                <button
                                    onClick={() => {
                                        if(window.confirm("Bu randevuyu iptal etmek istediğinize emin misiniz?")) {
                                            updateStatus('cancelled');
                                        }
                                    }}
                                    disabled={processing}
                                    className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-white dark:bg-navy-800 text-red-500 border border-red-200 dark:border-red-900/50 text-sm font-bold hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-50"
                                >
                                    {processing && selectedAppt.status !== 'pending' ? <div className="h-5 w-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin"/> : <><MdDelete className="text-xl"/> İptal Et</>}
                                </button>
                            )}
                        </div>

                    </div>
                </div>
            )}

            {/* Scrollbar CSS */}
            <style jsx>{`
                .custom-sidebar-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
                .custom-sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
                .custom-sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(163, 174, 208, 0.3); border-radius: 10px; }
                .dark .custom-sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); }

                @keyframes slideUp {
                    from { transform: translateY(100%); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-slideUp { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
            `}</style>
        </div>
    );
};

export default StaffCalendar;